import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { cleanOwner } from '../reports/scan-owner';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, catchError, concatMap, forkJoin, from, map, of, takeUntil } from 'rxjs';
import { UserApiService } from '../../service/userapi.service';
import { CardApiService } from '../../service/cardapi.service';
import { SubscriptionStateService } from '../../service/subscription-state.service';
import { Card, ScanTopCard } from '../../models/card.model';
import { User } from '../../models/user.model';
import { TrialStatusCardComponent } from '../../shared/subscription/trial-status-card.component';
import { PageHeaderComponent } from '../../shared/ui';
import { lockedUserCount, pendingInviteUsers, unassignedCardCount } from './pending-tasks';

/** Sade sayi karti (§1): yalniz etiket + sayi; renkli simge ve aciklama satiri kalkti. */
interface StatTile {
    key: 'users' | 'cards' | 'scansMonth' | 'contacts';
    labelKey: string;
    value: number;
}

/** SendBusinessVerificationEmail yanitindaki sayisal durum -> panel durumu (Kullanicilar §2 ile ayni sira). */
const USER_STATUS_BY_ID = ['Inactive', 'Pending', 'Active'];

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, TranslateModule, ChartModule, ButtonModule, SkeletonModule, ToastModule, ConfirmDialogModule, TrialStatusCardComponent, PageHeaderComponent],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast position="bottom-center" />
        <p-confirmdialog [style]="{ width: '450px' }" />

        <app-page-header [title]="'COMMON.DASHBOARD.TITLE' | translate" [description]="'COMMON.DASHBOARD.SUBTITLE' | translate">
            <p-button icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="loading()"
                [label]="'COMMON.REFRESH' | translate" (onClick)="load(true)" />
        </app-page-header>

        <app-trial-status-card />

        <!-- Sade sayi kartlari: etiket + sayi -->
        <div class="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            @for (t of tiles(); track t.key) {
                <div class="card !mb-0" data-testid="stat-tile" [attr.data-key]="t.key">
                    <span class="block text-surface-500 dark:text-surface-400 font-medium mb-2">
                        {{ t.labelKey | translate }}
                    </span>
                    @if (loading()) {
                        <p-skeleton width="4rem" height="2.25rem" />
                    } @else {
                        <div class="text-surface-900 dark:text-surface-0 font-bold text-3xl leading-none">
                            {{ t.value | number }}
                        </div>
                    }
                </div>
            }
        </div>

        <!-- Sizi bekleyenler: sayisi 0 olan satir, hepsi 0 ise kutu hic cizilmez (@if; [hidden] kullanilmaz) -->
        @if (!loading() && !loadError() && hasTodos()) {
            <div class="card !mb-4" data-testid="todo-box">
                <h5 class="mt-0 mb-3">{{ 'COMMON.DASHBOARD.TODO_TITLE' | translate }}</h5>
                <ul class="m-0 p-0 list-none">
                    @if (pendingInvites().length > 0) {
                        <li data-testid="todo-invites" class="kx-todo-row">
                            <span class="min-w-0">{{ 'COMMON.DASHBOARD.TODO_INVITES' | translate: { count: pendingInvites().length } }}</span>
                            <span data-testid="todo-resend" class="shrink-0">
                                <p-button icon="pi pi-envelope" size="small" [outlined]="true"
                                    [label]="'COMMON.DASHBOARD.RESEND_CODE' | translate"
                                    [loading]="resending()" [disabled]="resending() || !subState.canInvite()"
                                    (onClick)="resendPendingCodes()" />
                            </span>
                        </li>
                    }
                    @if (unassignedCount() > 0) {
                        <li data-testid="todo-unassigned" class="kx-todo-row">
                            <span class="min-w-0">{{ 'COMMON.DASHBOARD.TODO_UNASSIGNED' | translate: { count: unassignedCount() } }}</span>
                            <span data-testid="todo-assign" class="shrink-0">
                                <p-button icon="pi pi-user-plus" size="small" [outlined]="true"
                                    [label]="'COMMON.CARDS_PAGE.ASSIGN' | translate" (onClick)="openUnassignedCards()" />
                            </span>
                        </li>
                    }
                    @if (lockedCount() > 0) {
                        <li data-testid="todo-locked" class="kx-todo-row">
                            <span class="min-w-0">{{ 'COMMON.DASHBOARD.TODO_LOCKED' | translate: { count: lockedCount() } }}</span>
                            <span data-testid="todo-what" class="shrink-0">
                                <p-button icon="pi pi-question-circle" size="small" [text]="true" severity="secondary"
                                    [label]="'COMMON.DASHBOARD.WHAT_IS_IT' | translate"
                                    [attr.aria-expanded]="showLockedHelp()" (onClick)="showLockedHelp.set(!showLockedHelp())" />
                            </span>
                        </li>
                        @if (showLockedHelp()) {
                            <li data-testid="todo-locked-help" class="text-sm text-surface-500 dark:text-surface-400 pb-2">
                                {{ 'COMMON.DASHBOARD.LOCKED_EXPLAIN' | translate }}
                            </li>
                        }
                    }
                </ul>
            </div>
        }

        <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <!-- Aylik toplanan kontak grafigi -->
            <div class="card !mb-0 xl:col-span-2" data-testid="chart-card">
                <div class="flex items-center justify-between mb-4">
                    <h5 class="m-0">{{ 'COMMON.DASHBOARD.MONTHLY_CONTACTS' | translate }}</h5>
                    <span class="text-surface-400 dark:text-surface-500 text-sm">
                        {{ 'COMMON.DASHBOARD.LAST_6_MONTHS' | translate }}
                    </span>
                </div>
                @if (loading()) {
                    <p-skeleton width="100%" height="18rem" />
                } @else if (loadError()) {
                    <!-- KX-043: "yükleme başarısız" durumu "veri yok" ile karışmasın — hata + yeniden dene -->
                    <div class="text-center py-12">
                        <i class="pi pi-exclamation-triangle text-red-400 text-3xl"></i>
                        <p class="text-red-500 mt-2 mb-3">{{ 'COMMON.DASHBOARD.LOAD_FAILED' | translate }}</p>
                        <p-button icon="pi pi-refresh" [outlined]="true"
                            [label]="'COMMON.REFRESH' | translate" (onClick)="load(true)" />
                    </div>
                } @else if (hasChartData()) {
                    <p-chart type="bar" [data]="chartData()" [options]="chartOptions()" [style]="{ height: '20rem' }" />
                } @else {
                    <!-- §1: bos grafik buyuk alan kaplamaz, tek satir -->
                    <p data-testid="chart-empty" class="m-0 text-surface-500 dark:text-surface-400">
                        {{ 'COMMON.DASHBOARD.CHART_EMPTY' | translate }}
                    </p>
                }
            </div>

            <!-- En cok okutulan 5 kart -->
            <div class="card !mb-0" data-testid="top-cards-card">
                <div class="flex items-center justify-between mb-4">
                    <h5 class="m-0">{{ 'COMMON.DASHBOARD.TOP_CARDS' | translate }}</h5>
                </div>
                @if (loading()) {
                    <p-skeleton width="100%" height="18rem" />
                } @else if (topScans().length) {
                    <ul class="m-0 p-0 list-none flex flex-col gap-4">
                        @for (row of topScans(); track row.cardBase62) {
                            <li class="flex items-center justify-between gap-3">
                                <span class="text-surface-700 dark:text-surface-200 truncate">
                                    {{ topCardName(row) }}
                                </span>
                                <span class="text-surface-900 dark:text-surface-0 font-bold shrink-0">
                                    {{ row.count | number }}
                                </span>
                            </li>
                        }
                    </ul>
                } @else {
                    <p class="m-0 text-surface-500 dark:text-surface-400">
                        {{ 'COMMON.DASHBOARD.NO_SCANS' | translate }}
                    </p>
                }
            </div>
        </div>
    `,
    styles: [
        `
            .kx-todo-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.75rem;
                padding: 0.5rem 0;
                border-top: 1px solid var(--p-content-border-color);
            }
            .kx-todo-row:first-child {
                border-top: 0;
            }
        `
    ]
})
export class Dashboard implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private userApi = inject(UserApiService);
    private cardApi = inject(CardApiService);
    private translate = inject(TranslateService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private router = inject(Router);
    readonly subState = inject(SubscriptionStateService);

    // KX-055: tarayıcının yerel saat dilimi — GetScanSummary'nin ay sınırı yerel takvime göre
    // kovalansın diye sunucuya iletilir (bkz. cardapi.service.ts getScanSummary notu).
    private readonly timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    loading = signal(true);
    loadError = signal(false); // KX-043: "yükleme başarısız" ile "veri yok" ayrımı
    tiles = signal<StatTile[]>(this.initialTiles());
    chartData = signal<any>(null);
    chartOptions = signal<any>(this.buildChartOptions());
    hasChartData = signal(false);
    // En çok okutulan 5 kart (GetScanSummary.top); istek başarısız olursa boş kalır.
    topScans = signal<ScanTopCard[]>([]);
    // KX: base62 -> karttaki Ad Soyad. "top" listesi ScanTopCard.cardOwnerName ile kart SAHİBİNİN
    // (hesap) adını taşır; ama listede kartın ÜZERİNDEKİ kişinin adı gösterilmeli. Bu harita
    // load()'da yüklü `cards` listesinden (ileri-yön base62 eşleştirmesiyle) kurulur.
    cardNameByBase62 = signal<Map<string, string>>(new Map());

    // §1 "Sizi bekleyenler": load()'da zaten gelen kullanici/kart listelerinden sayilir (yeni istek yok).
    pendingInvites = signal<User[]>([]);
    unassignedCount = signal(0);
    lockedCount = signal(0);
    showLockedHelp = signal(false);
    resending = signal(false);

    // KX-042: dil değişiminde grafiği yeniden kurmak için elde tutulan veri (yeni HTTP yok)
    private lastContacts: Card[] = [];

    ngOnInit() {
        this.load();
        // Dil/tema değişiminde grafik eksen renklerini tazele
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.chartOptions.set(this.buildChartOptions());
            // KX-042: ay etiketleri ve dataset label'ı yeni dilde olsun — elde tutulan datayla yeniden kur.
            this.buildChart(this.lastContacts);
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    hasTodos(): boolean {
        return this.pendingInvites().length > 0 || this.unassignedCount() > 0 || this.lockedCount() > 0;
    }

    load(refresh = false) {
        this.loading.set(true);
        this.loadError.set(false);

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        forkJoin({
            users: this.userApi.loadUsers(refresh),
            cards: this.cardApi.getCards(refresh),
            shared: this.cardApi.getMySharedCards(refresh),
            // Okutma özeti ayrı hata yakalar: GetScanSummary çökerse dashboard'un geri kalanı
            // bozulmasın — okutma kutusu/kart listesi sessizce 0/boşa düşer (forkJoin bütünüyle
            // reddedilmez). Hata yine de konsola loglanır (bkz. aşağıdaki catchError) — sessiz yutma yok.
            scanSummary: this.cardApi.getScanSummary(monthStart, now, undefined, this.timeZone).pipe(
                catchError((err) => {
                    console.warn('Dashboard scan summary load failed', err);
                    return of(null);
                })
            )
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: ({ users, cards, shared, scanSummary }) => {
                const contacts = shared || [];

                this.tiles.set(this.initialTiles([
                    users?.length || 0,
                    cards?.length || 0,
                    scanSummary?.total || 0,
                    contacts.length
                ]));
                this.pendingInvites.set(pendingInviteUsers(users));
                this.unassignedCount.set(unassignedCardCount(cards, users));
                this.lockedCount.set(lockedUserCount(users));
                this.cardNameByBase62.set(this.buildCardNameMap(cards || []));
                this.topScans.set(scanSummary?.top || []);
                this.lastContacts = contacts;
                this.buildChart(contacts);
                this.loading.set(false);
            },
            // KX-043: sessiz hata yok — hata bayrağı + i18n toast; "veri yok" boş durumdan ayrışır.
            error: (err: unknown) => {
                // Panel QR F3/F8: hangi çağrının düştüğü teşhis edilebilsin diye loglanır (davranış aynı).
                console.error('Dashboard load failed', err);
                this.lastContacts = [];
                this.topScans.set([]);
                this.cardNameByBase62.set(new Map());
                this.pendingInvites.set([]);
                this.unassignedCount.set(0);
                this.lockedCount.set(0);
                this.loadError.set(true);
                this.loading.set(false);
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('COMMON.ERROR'),
                    detail: this.translate.instant('COMMON.DASHBOARD.LOAD_FAILED'),
                    life: 4000
                });
            }
        });
    }

    /**
     * "Kodu yeniden gönder" (§1): Kullanıcılar (§2) ile aynı tekil uç (SendBusinessVerificationEmail),
     * davetli başına sırayla. Birden çok davetlide önce onay istenir. Koltuk doluyken/abonelik
     * kapalıyken (canInvite) istek gitmez — Kullanıcılar sayfasındaki kilitle aynı.
     */
    resendPendingCodes() {
        const list = this.pendingInvites();
        if (list.length === 0 || this.resending() || !this.subState.canInvite()) return;
        if (list.length === 1) {
            this.runResend(list);
            return;
        }
        this.confirmationService.confirm({
            message: this.translate.instant('COMMON.USERS.CONFIRM_SEND_CODE_TO_ALL', { count: list.length }),
            header: this.translate.instant('COMMON.USERS.CONFIRM'),
            icon: 'pi pi-envelope',
            acceptLabel: this.translate.instant('COMMON.YES'),
            rejectLabel: this.translate.instant('COMMON.NO'),
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => this.runResend(list)
        });
    }

    private runResend(users: User[]) {
        this.resending.set(true);
        let success = 0;
        let failed = 0;
        from(users).pipe(
            concatMap(user =>
                this.userApi.sendEmail({ Id: user.id!, NameSurname: user.name!, Email: user.email! }).pipe(
                    map(resp => {
                        success++;
                        // KX-024: kullanıcı önbelleğine immutable yansıt (Kullanıcılar sayfası da güncel görsün)
                        const status = USER_STATUS_BY_ID[resp.status] ?? user.status ?? 'Pending';
                        const date = resp.emailSentDate?.toString() || user.date || '';
                        this.userApi.updateUserStatusAndDate(user.id!, status, date);
                        return true;
                    }),
                    catchError(err => {
                        failed++;
                        console.warn('Dashboard resend code failed', err);
                        return of(false);
                    })
                )
            ),
            takeUntil(this.destroy$)
        ).subscribe({
            complete: () => {
                this.resending.set(false);
                const single = users.length === 1;
                this.messageService.add({
                    severity: failed === 0 ? 'success' : single ? 'error' : 'warn',
                    summary: this.translate.instant(failed === 0 ? 'COMMON.USERS.SUCCESSFUL' : 'COMMON.DASHBOARD.RESEND_CODE'),
                    detail: single
                        ? this.translate.instant(failed === 0 ? 'COMMON.USERS.EMAIL_SENT' : 'COMMON.USERS.FAILED_TO_SEND_EMAIL')
                        : this.translate.instant('COMMON.USERS.BULK_SEND_RESULT', { success, failed }),
                    life: 4000
                });
            }
        });
    }

    /** "Kullanıcıya ata" (§1): Kartlar listesi "Atanmamış" çipi seçili açılır; atama oradaki mevcut akışla yapılır. */
    openUnassignedCards() {
        this.router.navigate(['/portfolio/cards'], { queryParams: { filter: 'unassigned' } });
    }

    private initialTiles(values: number[] = [0, 0, 0, 0]): StatTile[] {
        return [
            { key: 'users', labelKey: 'COMMON.DASHBOARD.TOTAL_USERS', value: values[0] },
            { key: 'cards', labelKey: 'COMMON.DASHBOARD.TOTAL_CARDS', value: values[1] },
            { key: 'scansMonth', labelKey: 'COMMON.DASHBOARD.SCANS_THIS_MONTH', value: values[2] },
            { key: 'contacts', labelKey: 'COMMON.DASHBOARD.COLLECTED_CONTACTS', value: values[3] }
        ];
    }

    // "En çok okutulan 5 kart" satırında gösterilecek adı seçer: önce kartın ÜZERİNDEKİ kişi adı
    // (cardNameByBase62, load()'da `cards`den kurulur), sonra sunucunun döndürdüğü kart SAHİBİ
    // (hesap) adı, en son da base62 kimliği (hiçbiri yoksa).
    topCardName(row: ScanTopCard): string {
        return this.cardNameByBase62().get(row.cardBase62) || cleanOwner('', row.cardOwnerName) || row.cardBase62;
    }

    // base62 -> "Ad Soyad" haritası kurar (ileri-yön eşleştirme, bkz. CardScanCount notu:
    // Base62ToGuid ters çevirimi güvenilir değil). Adı olmayan/id'si olmayan kartlar haritaya
    // girmez ki topCardName() sahip adı/base62 yedeğine düşebilsin.
    private buildCardNameMap(cards: Card[]): Map<string, string> {
        const map = new Map<string, string>();
        for (const card of cards) {
            if (!card?.id) continue;
            const key = this.cardApi.cardIdToBase62(card.id);
            if (!key) continue;
            const name = this.cardDisplayName(card);
            if (name) map.set(key, name);
        }
        return map;
    }

    // Kartın FirstName + LastName alan değerlerinden görüntü adını kurar (kart sahibi hesap adından
    // AYRI — bkz. Card.cardOwnerName vs. bu alanlar).
    private cardDisplayName(card: Card): string {
        const fn = card.fields?.find(f => f.fieldname === 'FirstName')?.value ?? '';
        const ln = card.fields?.find(f => f.fieldname === 'LastName')?.value ?? '';
        return `${fn} ${ln}`.trim();
    }

    // Kartex API zaman damgaları Windows FILETIME (1601-epoch, 100ns) döner; Unix ms/sn de tolere edilir.
    private toMs(ts?: number): number {
        if (!ts || ts <= 0) return 0;
        if (ts > 1e16) return ts / 10000 - 11644473600000;
        if (ts > 1e12) return ts;
        return ts * 1000;
    }

    private buildChart(contacts: Card[]) {
        const now = new Date();
        const lang = this.translate.currentLang || 'tr';
        const buckets: { key: string; label: string; count: number }[] = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            buckets.push({
                key: `${d.getFullYear()}-${d.getMonth()}`,
                label: d.toLocaleDateString(lang, { month: 'short', year: '2-digit' }),
                count: 0
            });
        }
        for (const c of contacts) {
            const ms = this.toMs(c.updateTimestamp);
            if (!ms) continue;
            const d = new Date(ms);
            const bucket = buckets.find(b => b.key === `${d.getFullYear()}-${d.getMonth()}`);
            if (bucket) bucket.count++;
        }

        const style = getComputedStyle(document.documentElement);
        const primary = style.getPropertyValue('--p-primary-color')?.trim() || '#3b82f6';
        this.chartData.set({
            labels: buckets.map(b => b.label),
            datasets: [{
                label: this.translate.instant('COMMON.DASHBOARD.COLLECTED_CONTACTS'),
                data: buckets.map(b => b.count),
                backgroundColor: primary,
                borderRadius: 6,
                maxBarThickness: 48
            }]
        });
        this.hasChartData.set(buckets.some(b => b.count > 0));
    }

    private buildChartOptions() {
        const style = getComputedStyle(document.documentElement);
        const muted = style.getPropertyValue('--p-text-muted-color')?.trim() || '#94a3b8';
        const border = style.getPropertyValue('--p-content-border-color')?.trim() || 'rgba(0,0,0,0.08)';
        return {
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
                x: { ticks: { color: muted }, grid: { display: false } },
                y: { beginAtZero: true, ticks: { color: muted, precision: 0 }, grid: { color: border } }
            }
        };
    }
}
